import { useState, useEffect, useRef } from 'react';
import {
    signIn, signOut, getCurrentUser,
    fetchAuthSession, fetchUserAttributes,
    updateUserAttributes, confirmSignIn, updatePassword
} from 'aws-amplify/auth';
import '.././amplifyConfig';

// Version of the education admin API the dashboard talks to. The settings
// flag needs v16 or later.
const API_VERSION = process.env.REACT_APP_API_VERSION || "v16";

// Where a super admin's last opened institution is remembered in this browser
const SUPER_INSTITUTION_KEY = "soapbox.superInstitution";

function readStoredInstitution(): string {
    try { return window.localStorage.getItem(SUPER_INSTITUTION_KEY) ?? ""; } catch { return ""; }
}


/**
 * Custom hook for handling authentication logic.
 * 
 * @returns {Object} - An object containing authentication state and handlers.
 */
function useAuth() {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [signInAllowed, setSignInAllowed] = useState(true);
    const [errorValue, setErrorValue] = useState("");
    const [signedIn, setSignedIn] = useState(false);
    const [signingIn, setSigningIn] = useState(false);
    const [soapboxData, setSoapboxData] = useState(null);
    const [idToken, setIDToken] = useState("");
    const [institution, setInstitution] = useState("");
    const [utcTimeEpoch, setUtcTimeEpoch] = useState(0);
    const [userAcceptedTcVersion, setUserAcceptedTcVersion] = useState("not-fetched");
    const latestTCVersion = "1.2";
    const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
    // True while a new account (created with a temporary password) has to choose
    // its own password before it can sign in
    const [newPasswordRequired, setNewPasswordRequired] = useState(false);
    // Super admins (Soapbox staff) can open any institution. The server decides who
    // is one; these only drive what the dashboard shows.
    const [superUser, setSuperUser] = useState(false);
    const [institutions, setInstitutions] = useState<string[]>([]);
    const [selectedInstitution, setSelectedInstitution] = useState(readStoredInstitution());
    // A ref as well, so refreshes started from timers always use the latest choice
    const selectedInstitutionRef = useRef(selectedInstitution);

    // The content endpoint, for the institution a super admin has chosen (ignored
    // by the server for everyone else)
    function contentUrl() {
        const base = `https://${process.env.REACT_APP_ENV_STAGE}.soapboxapis.com/education/${API_VERSION}/content`;
        const chosen = selectedInstitutionRef.current;
        return chosen ? `${base}?institution=${encodeURIComponent(chosen)}` : base;
    }

    async function selectInstitution(name: string) {
        selectedInstitutionRef.current = name;
        setSelectedInstitution(name);
        try { window.localStorage.setItem(SUPER_INSTITUTION_KEY, name); } catch { }
        await GetContent();
    }

    async function getUserAttributes() {
        try {
            const userAttributes = await fetchUserAttributes();
            const userLatestTcVersion = userAttributes['custom:tcVersion'];
            // console.log("tcVersion: ", userLatestTcVersion, "latestTCVersion: ", latestTCVersion);
            if (userLatestTcVersion) {
                setUserAcceptedTcVersion(userLatestTcVersion);
            }
        } catch (error) {
            console.error('Error fetching user attributes:', error);
        }
    }

    async function handleAcceptTerms() {
        try {
            await updateUserAttributes({
                userAttributes: {
                    'custom:tcVersion': latestTCVersion,
                },
            });
            setUserAcceptedTcVersion(latestTCVersion);
        } catch (error) {
            console.error('Error updating user attributes:', error);
        }
    }

    useEffect(() => {
        setShowTermsAndConditions(userAcceptedTcVersion !== latestTCVersion && userAcceptedTcVersion !== "not-fetched");
    }, [userAcceptedTcVersion, latestTCVersion,]);

    useEffect(() => {
        if (username !== "" && password !== "") setSignInAllowed(false);
        else setSignInAllowed(true);
    }, [username, password]);

    /**
    * Effect to fetch the main dashboard content if the user is signed in and content is not already fetched.
    */
    useEffect(() => {
        console.log("ID Token: ", idToken.substring(0, 20) + "...");
        // CheckCurrentSession();
        if (signedIn && idToken) {
            getUserAttributes();
            GetContent();
        }
    }, [signedIn, idToken]);

    useEffect(() => {
        CheckCurrentSession();
    }, []);

    async function handleSignIn(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setSigningIn(true);
        setSignInAllowed(false);
        setErrorValue("");
        const data = new FormData(event.currentTarget);
        let login = signIn({
            username: String(data.get("username")),
            password: String(data.get("password")),
            options: {
                authFlowType: 'USER_SRP_AUTH'
            }
        });
        login.then(
            async function (value) {
                if (value?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                    // Temporary password accepted: the user now has to choose their own
                    setNewPasswordRequired(true);
                    setSigningIn(false);
                    setSignInAllowed(true);
                    return;
                }
                if (!value?.isSignedIn) {
                    setErrorValue("Sign in could not be completed. Please contact support@soapbox.us.");
                    setSigningIn(false);
                    setSignInAllowed(true);
                    return;
                }
                await finishSignIn();
            },
            function (error) {
                console.error(error);
                setErrorValue(error.message);
                setSigningIn(false);
                setSignInAllowed(true);
            }
        );
    };

    // Loads the session once Cognito has signed the user in
    async function finishSignIn() {
        const session = await fetchAuthSession();
        var idToken = String(session?.tokens?.idToken?.toString());
        setIDToken(idToken);
        const currentUser = await getCurrentUser();
        setUsername(currentUser.username);
        getUserAttributes();
        setNewPasswordRequired(false);
        setSigningIn(false);
        setSignedIn(true);
    }

    /**
     * Second step of signing in for an account with a temporary password: sets the
     * password the user has chosen and completes the sign in.
     */
    async function completeNewPassword(newPassword: string) {
        setSigningIn(true);
        setErrorValue("");
        try {
            const result = await confirmSignIn({ challengeResponse: newPassword });
            if (result?.isSignedIn) await finishSignIn();
            else {
                setErrorValue("Sign in could not be completed. Please contact support@soapbox.us.");
                setSigningIn(false);
            }
        } catch (error: any) {
            console.error(error);
            setErrorValue(error?.message ?? "Unable to set the new password");
            setSigningIn(false);
        }
    }

    // Back to the normal sign in form
    function cancelNewPassword() {
        setNewPasswordRequired(false);
        setErrorValue("");
    }

    /**
     * Changes the signed in user's password. Returns an empty string when it worked,
     * otherwise the reason it did not.
     */
    async function changePassword(oldPassword: string, newPassword: string): Promise<string> {
        try {
            await updatePassword({ oldPassword, newPassword });
            return "";
        } catch (error: any) {
            console.error(error);
            if (error?.name === 'NotAuthorizedException') return "Current password is incorrect";
            return error?.message ?? "Unable to change the password";
        }
    }

    async function handleSignOut(event: React.FormEvent<HTMLFormElement>) {
        setSignedIn(false);
        event.preventDefault();
        setErrorValue("");
        setSoapboxData(null);
        setSuperUser(false);
        setInstitutions([]);
        await signOut();
        setSignedIn(false);
    };

    /**
     * Checks the current user session from Amplify Auth. 
     * Aligns the signedIn state with the current Auth session.
     */
    async function CheckCurrentSession() {
        try {
            const session = await fetchAuthSession();
            const idToken = String(session?.tokens?.idToken?.toString());
            const currentUser = await getCurrentUser();
            setIDToken(idToken);
            setSignedIn(true);
            setUsername(currentUser.username);
        } catch (error) {
            setSignedIn(false);
        }
    }

    /**
     * Fetches the education tracks content distribution from the Soapbox API.
     */
    async function GetContent() {

        const url = contentUrl();
        try {
            const response = await fetch(url, {
                headers: {
                    "Authorization": idToken
                }
            });
            if (!response.ok) {
                throw new Error(`Response status: ${response.status}`);
            }
            let data = await response.json();
            setSuperUser(data.superUser === true);
            setInstitutions(Array.isArray(data.institutions) ? data.institutions : []);
            // A super admin with no institution chosen yet gets no content: show an empty dashboard
            setSoapboxData(data.content ?? { artists: [], settings: {} });
            setInstitution(data.institution ?? null);
            setUtcTimeEpoch(data.utc);
            console.log(data);
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Super admins only: creates an institution and its dashboard admin login.
     * Returns what the server sent back: the new institution and admin details, or
     * an { error } describing why nothing was created.
     */
    async function createInstitution(name: string): Promise<any> {
        const url = `https://${process.env.REACT_APP_ENV_STAGE}.soapboxapis.com/education/${API_VERSION}/content?action=createInstitution`;
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    "Authorization": idToken,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ name })
            });
            let data: any = null;
            try { data = await response.json(); } catch { }
            if (!response.ok || !data || data.error) return { error: data?.error ?? `status_${response.status}` };
            // Open the new institution straight away (this also refreshes the list)
            await selectInstitution(data.institution.name);
            return data;
        } catch (error) {
            console.error(error);
            return { error: "network_error" };
        }
    }

    async function setTrackInstallAndVisibility(pdata: any): Promise<any> {
        const url = contentUrl();
        const content = JSON.stringify({ artists: pdata.artists, settings: pdata.settings }, null, 2);
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    "Authorization": idToken,
                    "Content-Type": "application/json"
                },
                body: content
            });
            if (!response.ok) {
                // Rejected (e.g. the selection is larger than the smallest headset's
                // storage). Nothing was saved, so reload what the server has.
                await GetContent();
                throw new Error(`Response status: ${response.status}`);
            }
            let data = await response.json();
            console.log("post request url: ", url);
            console.log("post request data: ", content);
            console.log("response: ", data);
            await GetContent();
            return data;
        } catch (error) {
            console.error(error);
        }
    }

    return {
        signedIn,
        username,
        soapboxData,
        institution,
        handleSignIn,
        handleSignOut,
        signingIn,
        signInAllowed,
        errorValue,
        setTrackInstallAndVisibility,
        idToken,
        GetContent,
        utcTimeEpoch,
        showTermsAndConditions,
        handleAcceptTerms,
        newPasswordRequired,
        completeNewPassword,
        cancelNewPassword,
        changePassword,
        superUser,
        institutions,
        selectedInstitution,
        selectInstitution,
        createInstitution,
    };
}

export default useAuth;